import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, Image, Check, X, Trash2, RefreshCw, Filter,
  User, Briefcase, Eye, Download, AlertCircle, Plus, ExternalLink,
  Send, FolderInput, Users, Camera, Receipt, FileSignature
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { uploadsAPI } from '../../services/api';
import UniversalUploader from '../common/UniversalUploader';
import api from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get full URL for file
const getFullFileUrl = (fileUrl) => {
  if (!fileUrl) return '';
  if (fileUrl.startsWith('http')) return fileUrl;
  
  // Fix legacy paths: /uploads/ should map to /api/files/
  if (fileUrl.startsWith('/uploads/')) {
    const fixedPath = fileUrl.replace('/uploads/', '/api/files/');
    return `${API_URL}${fixedPath}`;
  }
  
  // Already correct path or /api/files/ path
  return `${API_URL}${fileUrl}`;
};

const UploadsTab = () => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploader, setShowUploader] = useState(false);
  const [filter, setFilter] = useState({
    uploadType: '',
    uploaderRole: '',
    status: ''
  });
  const [selectedUpload, setSelectedUpload] = useState(null);
  
  // Send to modal state
  const [showSendToModal, setShowSendToModal] = useState(false);
  const [sendToUpload, setSendToUpload] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sendToDestination, setSendToDestination] = useState('');
  const [sendToTargetId, setSendToTargetId] = useState('');
  const [sendingFile, setSendingFile] = useState(false);

  const fetchUploads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.uploadType) params.upload_type = filter.uploadType;
      if (filter.uploaderRole) params.uploader_role = filter.uploaderRole;
      if (filter.status) params.status = filter.status;
      
      const response = await uploadsAPI.getAllUploads(params);
      setUploads(response.data.uploads || []);
    } catch (error) {
      console.error('Failed to fetch uploads:', error);
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkersAndCustomers = async () => {
    try {
      const [workersRes, customersRes] = await Promise.all([
        api.get('/workers'),
        api.get('/customer/list')
      ]);
      setWorkers(workersRes.data.workers || []);
      setCustomers(customersRes.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch workers/customers:', error);
    }
  };

  useEffect(() => {
    fetchUploads();
    fetchWorkersAndCustomers();
  }, [filter]);

  const openSendToModal = (upload) => {
    setSendToUpload(upload);
    setSendToDestination('');
    setSendToTargetId('');
    setShowSendToModal(true);
  };

  const handleSendTo = async () => {
    if (!sendToDestination) {
      toast.error('Please select a destination');
      return;
    }
    if ((sendToDestination === 'worker_file' || sendToDestination === 'customer_file') && !sendToTargetId) {
      toast.error('Please select a person');
      return;
    }
    
    setSendingFile(true);
    try {
      await api.post(`/uploads/${sendToUpload.id}/send-to`, {
        destination: sendToDestination,
        target_id: sendToTargetId || null
      });
      toast.success(`File sent to ${sendToDestination.replace('_', ' ')}`);
      setShowSendToModal(false);
      fetchUploads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send file');
    } finally {
      setSendingFile(false);
    }
  };

  const handleApprove = async (uploadId) => {
    try {
      await uploadsAPI.approve(uploadId);
      toast.success('Upload approved');
      fetchUploads();
    } catch (error) {
      toast.error('Failed to approve upload');
    }
  };

  const handleReject = async (uploadId) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      await uploadsAPI.reject(uploadId, reason);
      toast.success('Upload rejected');
      fetchUploads();
    } catch (error) {
      toast.error('Failed to reject upload');
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm('Delete this upload permanently?')) return;
    try {
      await uploadsAPI.delete(uploadId);
      toast.success('Upload deleted');
      fetchUploads();
    } catch (error) {
      toast.error('Failed to delete upload');
    }
  };

  const getUploadTypeLabel = (type) => {
    const labels = {
      'worker_document': 'Worker Document',
      'worker_job_photo': 'Job Photo',
      'worker_profile_photo': 'Profile Photo',
      'customer_service_photo': 'Service Photo',
      'customer_document': 'Customer Document',
      'drivers_license': "Driver's License",
      'receipt': 'Receipt',
      'certification': 'Certification',
      'policy': 'Policy Document'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'uploaded': 'bg-blue-500/20 text-blue-400',
      'approved': 'bg-green-500/20 text-green-400',
      'rejected': 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {status}
      </span>
    );
  };

  const isImage = (fileType) => fileType?.startsWith('image/');

  // Handle upload complete - refresh the list
  const handleUploadComplete = () => {
    fetchUploads();
    setShowUploader(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="uploads-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">File Uploads</h1>
          <p className="text-[var(--text-muted)] mt-1">Upload and manage documents and photos</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowUploader(!showUploader)} 
            className="gap-2 bg-[var(--primary)] text-black hover:bg-[var(--primary)]/90"
            data-testid="upload-new-btn"
          >
            <Plus className="w-4 h-4" />
            Upload New
          </Button>
          <Button onClick={fetchUploads} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Universal Uploader Section */}
      {showUploader && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-8"
        >
          <div className="bg-[var(--card-bg)] border border-[var(--border)] rounded-xl p-6">
            <UniversalUploader 
              userRole="owner" 
              onUploadComplete={handleUploadComplete}
            />
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select
          value={filter.uploadType}
          onChange={(e) => setFilter({ ...filter, uploadType: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white"
          data-testid="upload-type-filter"
        >
          <option value="">All Types</option>
          <option value="drivers_license">Driver's Licenses</option>
          <option value="worker_document">Worker Documents</option>
          <option value="worker_job_photo">Job Photos</option>
          <option value="customer_service_photo">Service Photos</option>
          <option value="customer_document">Customer Documents</option>
          <option value="receipt">Receipts</option>
          <option value="certification">Certifications</option>
          <option value="policy">Policies</option>
        </select>

        <select
          value={filter.uploaderRole}
          onChange={(e) => setFilter({ ...filter, uploaderRole: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white"
          data-testid="uploader-role-filter"
        >
          <option value="">All Uploaders</option>
          <option value="owner">Owner</option>
          <option value="worker">Workers</option>
          <option value="customer">Customers</option>
        </select>

        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white"
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="uploaded">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card p-4 text-center">
          <Upload className="w-6 h-6 mx-auto mb-2 text-blue-400" />
          <p className="text-2xl font-bold">{uploads.length}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Uploads</p>
        </div>
        <div className="card p-4 text-center">
          <AlertCircle className="w-6 h-6 mx-auto mb-2 text-yellow-400" />
          <p className="text-2xl font-bold">{uploads.filter(u => u.status === 'uploaded').length}</p>
          <p className="text-sm text-[var(--text-muted)]">Pending Review</p>
        </div>
        <div className="card p-4 text-center">
          <Briefcase className="w-6 h-6 mx-auto mb-2 text-[#D4AF37]" />
          <p className="text-2xl font-bold">{uploads.filter(u => u.uploader_role === 'worker').length}</p>
          <p className="text-sm text-[var(--text-muted)]">Worker Uploads</p>
        </div>
        <div className="card p-4 text-center">
          <User className="w-6 h-6 mx-auto mb-2 text-[#F4C2C2]" />
          <p className="text-2xl font-bold">{uploads.filter(u => u.uploader_role === 'customer').length}</p>
          <p className="text-sm text-[var(--text-muted)]">Customer Uploads</p>
        </div>
      </div>

      {/* Uploads Grid */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#D4AF37]" />
          <p className="mt-4 text-[var(--text-muted)]">Loading uploads...</p>
        </div>
      ) : uploads.length === 0 ? (
        <div className="text-center py-12 card">
          <Upload className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-4" />
          <p className="text-[var(--text-muted)]">No uploads found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {uploads.map((upload) => (
            <motion.div
              key={upload.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card overflow-hidden"
              data-testid={`upload-${upload.id}`}
            >
              {/* Preview */}
              <div className="relative h-48 bg-black/30">
                {isImage(upload.file_type) ? (
                  <img
                    src={getFullFileUrl(upload.file_url)}
                    alt={upload.file_name}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => setSelectedUpload(upload)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-full h-full flex items-center justify-center ${isImage(upload.file_type) ? 'hidden' : ''}`}>
                  <FileText className="w-16 h-16 text-[var(--text-muted)]" />
                </div>
                <div className="absolute top-2 right-2">
                  {getStatusBadge(upload.status)}
                </div>
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    upload.uploader_role === 'worker' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#F4C2C2]/20 text-[#F4C2C2]'
                  }`}>
                    {upload.uploader_role}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-semibold mb-1 truncate">{upload.file_name}</h3>
                <p className="text-sm text-[var(--text-muted)] mb-2">
                  {getUploadTypeLabel(upload.upload_type)}
                  {upload.document_type && ` • ${upload.document_type}`}
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  By: {upload.uploader_name}
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  {new Date(upload.created_at).toLocaleDateString()}
                </p>
                {upload.description && (
                  <p className="text-xs text-[var(--text-muted)] mt-2 italic">
                    &ldquo;{upload.description}&rdquo;
                  </p>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <a
                    href={getFullFileUrl(upload.file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary flex-1 text-center py-2 text-sm flex items-center justify-center gap-1"
                    data-testid={`view-${upload.id}`}
                  >
                    <Eye className="w-4 h-4" /> View
                  </a>
                  <a
                    href={getFullFileUrl(upload.file_url)}
                    download={upload.file_name}
                    className="btn-secondary px-3 py-2 text-sm flex items-center"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openSendToModal(upload)}
                    className="text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                    title="Send to worker/customer file"
                    data-testid={`send-to-${upload.id}`}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                  {upload.status === 'uploaded' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(upload.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-${upload.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReject(upload.id)}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid={`reject-${upload.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(upload.id)}
                    className="text-red-400 hover:text-red-300"
                    data-testid={`delete-${upload.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedUpload && isImage(selectedUpload.file_type) && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedUpload(null)}
        >
          <div className="max-w-4xl max-h-[90vh] relative">
            <img
              src={getFullFileUrl(selectedUpload.file_url)}
              alt={selectedUpload.file_name}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <Button
              className="absolute top-2 right-2"
              onClick={() => setSelectedUpload(null)}
            >
              <X className="w-5 h-5" />
            </Button>
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-4">
              <p className="text-white font-medium">{selectedUpload.file_name}</p>
              <p className="text-white/70 text-sm">
                {getUploadTypeLabel(selectedUpload.upload_type)} • {selectedUpload.uploader_name}
              </p>
              <a 
                href={getFullFileUrl(selectedUpload.file_url)} 
                download={selectedUpload.file_name}
                className="inline-flex items-center gap-1 mt-2 text-[var(--primary)] hover:underline text-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="w-4 h-4" /> Download
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Send To Modal */}
      <AnimatePresence>
        {showSendToModal && sendToUpload && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowSendToModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl w-full max-w-md p-6"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Send className="w-6 h-6 text-[#D4AF37]" />
                Send File To
              </h2>
              
              <div className="mb-4 p-3 bg-[var(--background-subtle)] rounded-lg">
                <p className="text-sm font-medium truncate">{sendToUpload.file_name}</p>
                <p className="text-xs text-[var(--text-muted)]">{getUploadTypeLabel(sendToUpload.upload_type)}</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">Destination</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => { setSendToDestination('photo_gallery'); setSendToTargetId(''); }}
                      className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-2 ${
                        sendToDestination === 'photo_gallery' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[var(--border-primary)] hover:border-[#D4AF37]/50'
                      }`}
                    >
                      <Camera className="w-6 h-6" />
                      <span className="text-sm font-medium">Photo Gallery</span>
                    </button>
                    <button
                      onClick={() => { setSendToDestination('receipts'); setSendToTargetId(''); }}
                      className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-2 ${
                        sendToDestination === 'receipts' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[var(--border-primary)] hover:border-[#D4AF37]/50'
                      }`}
                    >
                      <Receipt className="w-6 h-6" />
                      <span className="text-sm font-medium">Receipts</span>
                    </button>
                    <button
                      onClick={() => setSendToDestination('worker_file')}
                      className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-2 ${
                        sendToDestination === 'worker_file' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[var(--border-primary)] hover:border-[#D4AF37]/50'
                      }`}
                    >
                      <Briefcase className="w-6 h-6" />
                      <span className="text-sm font-medium">Worker File</span>
                    </button>
                    <button
                      onClick={() => setSendToDestination('customer_file')}
                      className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-2 ${
                        sendToDestination === 'customer_file' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[var(--border-primary)] hover:border-[#D4AF37]/50'
                      }`}
                    >
                      <User className="w-6 h-6" />
                      <span className="text-sm font-medium">Customer File</span>
                    </button>
                  </div>
                </div>
                
                {/* Worker selector */}
                {sendToDestination === 'worker_file' && (
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">Select Worker</label>
                    <select
                      value={sendToTargetId}
                      onChange={(e) => setSendToTargetId(e.target.value)}
                      className="input-styled w-full"
                    >
                      <option value="">-- Select a worker --</option>
                      {workers.map(worker => (
                        <option key={worker.id} value={worker.id}>{worker.full_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Customer selector */}
                {sendToDestination === 'customer_file' && (
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-2 block">Select Customer</label>
                    <select
                      value={sendToTargetId}
                      onChange={(e) => setSendToTargetId(e.target.value)}
                      className="input-styled w-full"
                    >
                      <option value="">-- Select a customer --</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>{customer.full_name || customer.email}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowSendToModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendTo} 
                  disabled={sendingFile || !sendToDestination}
                  className="btn-primary flex-1"
                >
                  {sendingFile ? 'Sending...' : 'Send File'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default UploadsTab;
